/**
 * Getypeerde toegang tot de prijsengine: regels, evenementen, de kalender en de
 * simulatie.
 *
 * Twee dingen die hier geen implementatiedetail zijn.
 *
 * **De simulatie is een eigen aanroep.** Niet een vlag op de kalender: wat er nú
 * geldt en wat er zóu gelden zijn twee vragen, en ze door elkaar kunnen halen is
 * precies wat "simulatie vóór activatie" moet voorkomen.
 *
 * **Een regel wordt ingetrokken, niet verwijderd.** De reserveringen die eronder zijn
 * gemaakt dragen hun eigen bevroren prijs; de regel is het antwoord op "waarom kostte
 * die nacht dat".
 */
import { call, isRecord, readCount, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type PricingFailure = Failure
export type PricingResult<T> = Result<T>

/** Exact de CHECK op `pricing_rules.kind` (migratie 0038). */
export const RULE_KINDS = ['season', 'weekday', 'event', 'occupancy', 'lead_time'] as const
export type RuleKind = (typeof RULE_KINDS)[number]

/** Alleen bij deze soorten hoort een datumbereik. */
export const DATED_KINDS: readonly RuleKind[] = ['season']

export const RULE_STATUSES = ['draft', 'active', 'retired'] as const
export type RuleStatus = (typeof RULE_STATUSES)[number]

export interface PricingRule {
  readonly id: string
  readonly kind: RuleKind
  readonly label: string
  readonly status: RuleStatus
  readonly adjustPermille: number
  readonly priority: number
  readonly startsOn: string | null
  readonly endsOn: string | null
  readonly weekdays: readonly number[] | null
  readonly eventId: string | null
  readonly eventName: string
  readonly occupancyBelowPermille: number | null
  readonly occupancyAbovePermille: number | null
  readonly leadTimeWithinDays: number | null
  readonly leadTimeBeyondDays: number | null
}

export interface PricingEvent {
  readonly id: string
  readonly name: string
  readonly startsOn: string
  readonly endsOn: string
  readonly sourceUrl: string
  /** Waar of niet: van het platform. Die kan een verhuurder niet weghalen. */
  readonly fromArea: boolean
}

export interface Contribution {
  readonly ruleId: string
  readonly kind: RuleKind
  readonly label: string
  readonly adjustPermille: number
}

export interface NightPrice {
  readonly date: string
  readonly baseCents: number
  readonly adjustPermille: number
  readonly adjustedCents: number
  readonly rateCents: number
  readonly clamped: boolean
  readonly contributions: readonly Contribution[]
}

export interface RateCalendar {
  readonly currency: string
  readonly baseRateCents: number
  readonly nights: readonly NightPrice[]
}

export interface Simulation {
  readonly currency: string
  readonly baseRateCents: number
  readonly current: readonly NightPrice[]
  readonly proposed: readonly NightPrice[]
  readonly currentTotalCents: number
  readonly proposedTotalCents: number
  readonly differenceCents: number
}

function isOneOf<T extends string>(value: unknown, known: readonly T[]): value is T {
  return typeof value === 'string' && (known as readonly string[]).includes(value)
}

function readNullableText(source: Record<string, unknown>, field: string): string | null {
  const raw = source[field]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readNullableCount(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

/** Promille komt binnen als geheel getal; het kan negatief zijn (een korting). */
function readSigned(source: Record<string, unknown>, field: string): number {
  const raw = source[field]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
}

function readRule(value: unknown): PricingRule | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const kind = value['kind']
  const status = value['status']
  // Een regel met een onbekende soort wordt overgeslagen: hem als iets anders tonen
  // zou betekenen dat het scherm een prijs verklaart met een regel die er niet is.
  if (id === '' || !isOneOf(kind, RULE_KINDS) || !isOneOf(status, RULE_STATUSES)) {
    return null
  }
  const weekdays = value['weekdays']
  return {
    id,
    kind,
    status,
    label: readText(value, 'label'),
    adjustPermille: readSigned(value, 'adjustPermille'),
    priority: readSigned(value, 'priority'),
    startsOn: readNullableText(value, 'startsOn'),
    endsOn: readNullableText(value, 'endsOn'),
    weekdays: Array.isArray(weekdays)
      ? weekdays.filter((day): day is number => typeof day === 'number')
      : null,
    eventId: readNullableText(value, 'eventId'),
    eventName: readText(value, 'eventName'),
    occupancyBelowPermille: readNullableCount(value, 'occupancyBelowPermille'),
    occupancyAbovePermille: readNullableCount(value, 'occupancyAbovePermille'),
    leadTimeWithinDays: readNullableCount(value, 'leadTimeWithinDays'),
    leadTimeBeyondDays: readNullableCount(value, 'leadTimeBeyondDays'),
  }
}

function readEvent(value: unknown): PricingEvent | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  if (id === '') {
    return null
  }
  return {
    id,
    name: readText(value, 'name'),
    startsOn: readText(value, 'startsOn'),
    endsOn: readText(value, 'endsOn'),
    sourceUrl: readText(value, 'sourceUrl'),
    fromArea: value['fromArea'] === true,
  }
}

function readNight(value: unknown): NightPrice | null {
  if (!isRecord(value)) {
    return null
  }
  const date = readText(value, 'date')
  if (date === '') {
    return null
  }
  const contributions: Contribution[] = []
  if (Array.isArray(value['contributions'])) {
    for (const entry of value['contributions']) {
      if (!isRecord(entry)) {
        continue
      }
      const kind = entry['kind']
      if (!isOneOf(kind, RULE_KINDS)) {
        continue
      }
      contributions.push({
        kind,
        ruleId: readText(entry, 'ruleId'),
        label: readText(entry, 'label'),
        adjustPermille: readSigned(entry, 'adjustPermille'),
      })
    }
  }
  return {
    date,
    baseCents: readCount(value, 'baseCents'),
    adjustPermille: readSigned(value, 'adjustPermille'),
    adjustedCents: readCount(value, 'adjustedCents'),
    rateCents: readCount(value, 'rateCents'),
    clamped: value['clamped'] === true,
    contributions,
  }
}

function readNights(payload: unknown, key: string): NightPrice[] {
  if (!isRecord(payload) || !Array.isArray(payload[key])) {
    return []
  }
  const nights: NightPrice[] = []
  for (const entry of payload[key]) {
    const night = readNight(entry)
    if (night !== null) {
      nights.push(night)
    }
  }
  return nights
}

function readList<T>(key: string, read: (value: unknown) => T | null) {
  return (payload: unknown): T[] | null => {
    if (!isRecord(payload) || !Array.isArray(payload[key])) {
      return null
    }
    const items: T[] = []
    for (const entry of payload[key]) {
      const item = read(entry)
      if (item !== null) {
        items.push(item)
      }
    }
    return items
  }
}

function base(residenceId: string): string {
  return `/residences/${encodeURIComponent(residenceId)}/pricing`
}

function rangeQuery(range: { readonly from: string; readonly to: string }): string {
  return `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
}

export async function fetchCalendar(
  residenceId: string,
  range: { readonly from: string; readonly to: string },
  options: RequestOptions = {},
): Promise<PricingResult<RateCalendar>> {
  return call(
    'GET',
    `${base(residenceId)}/calendar${rangeQuery(range)}`,
    undefined,
    (payload) =>
      isRecord(payload)
        ? {
            currency: readText(payload, 'currency'),
            baseRateCents: readCount(payload, 'baseRateCents'),
            nights: readNights(payload, 'nights'),
          }
        : null,
    options,
  )
}

export async function fetchSimulation(
  residenceId: string,
  range: { readonly from: string; readonly to: string },
  options: RequestOptions = {},
): Promise<PricingResult<Simulation>> {
  return call(
    'GET',
    `${base(residenceId)}/simulation${rangeQuery(range)}`,
    undefined,
    (payload) =>
      isRecord(payload)
        ? {
            currency: readText(payload, 'currency'),
            baseRateCents: readCount(payload, 'baseRateCents'),
            current: readNights(payload, 'current'),
            proposed: readNights(payload, 'proposed'),
            currentTotalCents: readCount(payload, 'currentTotalCents'),
            proposedTotalCents: readCount(payload, 'proposedTotalCents'),
            differenceCents: readSigned(payload, 'differenceCents'),
          }
        : null,
    options,
  )
}

export async function listRules(
  residenceId: string,
  options: RequestOptions = {},
): Promise<PricingResult<readonly PricingRule[]>> {
  return call('GET', `${base(residenceId)}/rules`, undefined, readList('rules', readRule), options)
}

export interface RuleInput {
  readonly kind: RuleKind
  readonly label: string
  readonly status: RuleStatus
  readonly adjustPermille: number
  readonly priority: number
  readonly startsOn?: string | null
  readonly endsOn?: string | null
  readonly weekdays?: readonly number[] | null
  readonly eventId?: string | null
  readonly occupancyBelowPermille?: number | null
  readonly occupancyAbovePermille?: number | null
  readonly leadTimeWithinDays?: number | null
  readonly leadTimeBeyondDays?: number | null
}

export async function createRule(
  residenceId: string,
  input: RuleInput,
): Promise<PricingResult<readonly PricingRule[]>> {
  return call('POST', `${base(residenceId)}/rules`, input, readList('rules', readRule))
}

export async function updateRule(
  residenceId: string,
  ruleId: string,
  input: RuleInput,
): Promise<PricingResult<readonly PricingRule[]>> {
  return call(
    'PUT',
    `${base(residenceId)}/rules/${encodeURIComponent(ruleId)}`,
    input,
    readList('rules', readRule),
  )
}

export async function retireRule(
  residenceId: string,
  ruleId: string,
): Promise<PricingResult<readonly PricingRule[]>> {
  return call(
    'DELETE',
    `${base(residenceId)}/rules/${encodeURIComponent(ruleId)}`,
    undefined,
    readList('rules', readRule),
  )
}

export async function listEvents(
  residenceId: string,
  options: RequestOptions = {},
): Promise<PricingResult<readonly PricingEvent[]>> {
  return call(
    'GET',
    `${base(residenceId)}/events`,
    undefined,
    readList('events', readEvent),
    options,
  )
}

export async function createEvent(
  residenceId: string,
  input: {
    readonly name: string
    readonly startsOn: string
    readonly endsOn: string
    readonly sourceUrl: string
  },
): Promise<PricingResult<readonly PricingEvent[]>> {
  return call('POST', `${base(residenceId)}/events`, input, readList('events', readEvent))
}

export async function retireEvent(
  residenceId: string,
  eventId: string,
): Promise<PricingResult<readonly PricingEvent[]>> {
  return call(
    'DELETE',
    `${base(residenceId)}/events/${encodeURIComponent(eventId)}`,
    undefined,
    readList('events', readEvent),
  )
}

export async function saveBounds(
  residenceId: string,
  minRateCents: number | null,
  maxRateCents: number | null,
): Promise<PricingResult<true>> {
  return call('PUT', `${base(residenceId)}/bounds`, { minRateCents, maxRateCents }, () => true)
}

/**
 * Promille als leesbaar percentage, met teken.
 *
 * +400 wordt "+40%". Het teken staat erbij omdat een regel ook een korting kan zijn,
 * en "40%" zonder teken laat in het midden welke kant het op gaat.
 */
export function formatAdjustment(permille: number): string {
  const percent = permille / 10
  const sign = permille > 0 ? '+' : ''
  return `${sign}${String(Number(percent.toFixed(1)))}%`
}

export function pricingErrorMessageKey(failure: PricingFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.pricing.error.rejected'
    case 'not_found':
      return 'dashboard.pricing.error.notFound'
    case 'forbidden':
      return 'dashboard.pricing.error.forbidden'
    default:
      return 'dashboard.pricing.error.unavailable'
  }
}
